import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth, ROLE_PERMISSIONS } from "@/hooks/useAuth";

const navItems = [
  {
    key: "dashboard",
    label: "系统总览",
    icon: "ri-dashboard-3-line",
    path: "/",
  },
  {
    key: "customer",
    label: "客户管理",
    icon: "ri-user-3-line",
    path: "/profile",
    children: [
      { label: "居民用户", path: "/profile/resident", icon: "ri-home-4-line" },
      { label: "工商业用户", path: "/profile/commercial", icon: "ri-building-2-line" },
    ],
  },
  {
    key: "meter",
    label: "表具管理",
    icon: "ri-cpu-line",
    path: "/meter",
    children: [
      { label: "实时监控", path: "/meter/realtime", icon: "ri-live-line" },
      { label: "设备台账", path: "/meter/device", icon: "ri-list-check-2" },
      { label: "历史记录", path: "/meter/history", icon: "ri-history-line" },
    ],
  },
  {
    key: "sales",
    label: "营业收费",
    icon: "ri-gas-station-line",
    path: "/sales",
    children: [
      { label: "购气操作", path: "/sales/purchase", icon: "ri-shopping-cart-2-line" },
      { label: "售气记录", path: "/sales/records", icon: "ri-file-list-3-line" },
      { label: "收费窗口", path: "/payment", icon: "ri-bank-card-line" },
    ],
  },
  {
    key: "fee",
    label: "费用管理",
    icon: "ri-money-cny-circle-line",
    path: "/fee",
    children: [
      { label: "费用查询", path: "/fee/query", icon: "ri-search-line" },
      { label: "欠费管理", path: "/fee/arrears", icon: "ri-error-warning-line" },
      { label: "气价管理", path: "/price/ladder", icon: "ri-price-tag-3-line" },
      { label: "气价下发", path: "/price/dispatch", icon: "ri-send-plane-line" },
    ],
  },
  {
    key: "bank",
    label: "银行代扣",
    icon: "ri-bank-line",
    path: "/bank",
  },
  {
    key: "safety",
    label: "安检管理",
    icon: "ri-shield-check-line",
    path: "/safety",
    children: [
      { label: "安检计划", path: "/safety", icon: "ri-shield-check-line" },
      { label: "安检员工作台", path: "/safety/inspector", icon: "ri-smartphone-line" },
    ],
  },
  {
    key: "install",
    label: "报装业务",
    icon: "ri-tools-line",
    path: "/install",
  },
  {
    key: "report",
    label: "报表管理",
    icon: "ri-bar-chart-box-line",
    path: "/report",
    children: [
      { label: "营收报表", path: "/report/daily", icon: "ri-calendar-line" },
      { label: "AI智能报告", path: "/report/ai", icon: "ri-robot-2-line" },
      { label: "自定义报表", path: "/report/custom", icon: "ri-settings-4-line" },
    ],
  },
  {
    key: "query",
    label: "查询统计",
    icon: "ri-search-eye-line",
    path: "/query",
    children: [
      { label: "GIS一张图", path: "/gis", icon: "ri-map-2-line" },
      { label: "数据大屏", path: "/screen", icon: "ri-tv-2-line" },
      { label: "实时告警", path: "/alarm", icon: "ri-alarm-warning-line" },
    ],
  },
  {
    key: "sms",
    label: "短信催缴",
    icon: "ri-message-2-line",
    path: "/sms",
  },
  {
    key: "material",
    label: "材料管理",
    icon: "ri-archive-line",
    path: "/material",
  },
  {
    key: "online",
    label: "网上查询缴费",
    icon: "ri-global-line",
    path: "/mobile",
  },
  {
    key: "service",
    label: "客户服务中心",
    icon: "ri-customer-service-2-line",
    path: "/service",
  },
  {
    key: "saas",
    label: "SaaS管理平台",
    icon: "ri-cloud-line",
    path: "/saas",
  },
  {
    key: "settings",
    label: "系统管理",
    icon: "ri-settings-3-line",
    path: "/settings",
  },
];

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const allowedKeys = user?.role ? ROLE_PERMISSIONS[user.role] : ROLE_PERMISSIONS["operator"];
  const filteredNavItems = navItems.filter(item => allowedKeys.includes(item.key));

  const toggleExpand = (key: string) => {
    setExpandedKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <aside className="w-56 min-h-screen bg-[#0f1729] flex flex-col flex-shrink-0">
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-4 py-4 border-b border-white/10">
        <img
          src="https://static.readdy.ai/image/99ef796e69792e9f0498176b3edc31a5/c291fd235bf5ccab8f3a62de9d2613bd.png"
          alt="智慧燃气Logo"
          className="w-8 h-8 object-contain rounded-lg"
        />
        <div>
          <div className="text-white font-bold text-xs leading-tight">AI智慧燃气</div>
          <div className="text-white/40 text-[10px]">营收管理系统 v3.0</div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-2 overflow-y-auto scrollbar-thin">
        {filteredNavItems.map(item => (
          <div key={item.key}>
            {item.children ? (
              <>
                <button
                  onClick={() => toggleExpand(item.key)}
                  className={`w-full flex items-center justify-between px-4 py-2 text-xs transition-colors cursor-pointer ${
                    isActive(item.path)
                      ? "text-white"
                      : "text-white/50 hover:text-white/80"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${item.icon} text-sm`}></i>
                    </div>
                    <span className="font-medium">{item.label}</span>
                  </div>
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className={`ri-arrow-right-s-line text-xs transition-transform ${expandedKeys.includes(item.key) ? "rotate-90" : ""}`}></i>
                  </div>
                </button>
                {expandedKeys.includes(item.key) && (
                  <div className="ml-4 border-l border-white/10 pl-3 mb-0.5">
                    {item.children.map(child => (
                      <NavLink
                        key={child.path + child.label}
                        to={child.path}
                        className={({ isActive: active }) =>
                          `flex items-center gap-2 px-2.5 py-1.5 text-[11px] rounded-md mb-0.5 transition-colors cursor-pointer ${
                            active && location.pathname === child.path
                              ? "bg-[#E8531A]/20 text-[#E8531A]"
                              : "text-white/40 hover:text-white/70 hover:bg-white/5"
                          }`
                        }
                      >
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className={`${child.icon} text-xs`}></i>
                        </div>
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <NavLink
                to={item.path}
                end={item.path === "/"}
                className={({ isActive: active }) =>
                  `flex items-center gap-2.5 px-4 py-2 text-xs transition-colors cursor-pointer ${
                    active
                      ? "text-white bg-white/10 border-r-2 border-[#E8531A]"
                      : "text-white/50 hover:text-white/80 hover:bg-white/5"
                  }`
                }
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${item.icon} text-sm`}></i>
                </div>
                <span className="font-medium">{item.label}</span>
              </NavLink>
            )}
          </div>
        ))}
      </nav>

      {/* Bottom */}
      <div className="px-3 py-3 border-t border-white/10">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-7 h-7 rounded-full bg-[#E8531A] flex items-center justify-center flex-shrink-0">
            <span className="text-white text-[10px] font-bold">{user?.displayName?.[0] || "管"}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white text-[11px] font-medium truncate">{user?.displayName || "系统管理员"}</div>
            <div className="text-white/40 text-[10px] truncate">{user?.tenant || "朝阳燃气公司"}</div>
          </div>
          <button
            onClick={() => navigate("/screen")}
            className="w-5 h-5 flex items-center justify-center cursor-pointer"
            title="数据大屏"
          >
            <i className="ri-tv-2-line text-white/40 hover:text-[#E8531A] text-sm transition-colors"></i>
          </button>
        </div>
        <button
          onClick={() => { logout(); navigate("/login"); }}
          className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/5 text-[11px] transition-colors cursor-pointer"
        >
          <i className="ri-logout-box-r-line text-sm"></i>
          <span>退出登录</span>
        </button>
      </div>
    </aside>
  );
}
